// Comprehensive Performance Monitoring System
import { trackEvent } from './analytics';

export interface PerformanceMetrics {
  // Core Web Vitals
  lcp: number; // Largest Contentful Paint
  fid: number; // First Input Delay
  cls: number; // Cumulative Layout Shift
  ttfb: number; // Time to First Byte
  fcp: number; // First Contentful Paint
  
  // Custom metrics
  pageLoadTime: number;
  apiResponseTime: number;
  imageLoadTime: number;
  bundleLoadTime: number;
  
  // Memory and resource usage
  memoryUsage: number;
  networkRequests: number;
  domNodes: number;
  
  // User experience metrics
  timeToInteractive: number;
  timeToFirstMeaningfulPaint: number;
}

export interface PerformanceThresholds {
  lcp: number; // 2.5s
  fid: number; // 100ms
  cls: number; // 0.1
  ttfb: number; // 800ms
  fcp: number; // 1.8s
}

export class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private metrics: Map<string, PerformanceMetrics> = new Map();
  private observers: Set<(metrics: PerformanceMetrics) => void> = new Set();
  private thresholds: PerformanceThresholds = {
    lcp: 2500,
    fid: 100,
    cls: 0.1,
    ttfb: 800,
    fcp: 1800,
  };

  static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  // Initialize performance monitoring
  init(): void {
    if (typeof window === 'undefined') return;

    // Add safety checks and error handling
    try {
      this.observeCoreWebVitals();
      this.observeCustomMetrics();
      this.observeResourceTiming();
      this.observeMemoryUsage();
    } catch (error) {
      console.warn('Performance monitoring initialization failed:', error);
    }
  }

  // Observe Core Web Vitals
  private observeCoreWebVitals(): void {
    // Largest Contentful Paint
    if ('PerformanceObserver' in window && document && document.body) {
      try {
        const lcpObserver = new PerformanceObserver((entryList) => {
          const entries = entryList.getEntries();
          const lastEntry = entries[entries.length - 1] as PerformanceEntry;
          if (lastEntry) {
            this.trackMetric('lcp', lastEntry.startTime);
          }
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
      } catch (error) {
        console.warn('LCP observer failed:', error);
      }

      // First Input Delay
      try {
        const fidObserver = new PerformanceObserver((entryList) => {
          const entries = entryList.getEntries();
          entries.forEach((entry) => {
            this.trackMetric('fid', entry.processingStart - entry.startTime);
          });
        });
        fidObserver.observe({ entryTypes: ['first-input'] });
      } catch (error) {
        console.warn('FID observer failed:', error);
      }

      // Cumulative Layout Shift
      try {
        const clsObserver = new PerformanceObserver((entryList) => {
          let clsValue = 0;
          const entries = entryList.getEntries();
          entries.forEach((entry: any) => {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
            }
          });
          this.trackMetric('cls', clsValue);
        });
        clsObserver.observe({ entryTypes: ['layout-shift'] });
      } catch (error) {
        console.warn('CLS observer failed:', error);
      }
    }
  }

  // Observe custom performance metrics
  private observeCustomMetrics(): void {
    if (typeof window === 'undefined') return;

    // Page load time
    window.addEventListener('load', () => {
      const loadTime = performance.now();
      this.trackMetric('pageLoadTime', loadTime);
      
      // Track to analytics
      trackEvent('performance', {
        event_category: 'performance',
        metric: 'page_load_time',
        value: Math.round(loadTime),
        page: window.location.pathname,
        user_type: 'guest',
      });
    });

    // DOM content loaded
    document.addEventListener('DOMContentLoaded', () => {
      const domReadyTime = performance.now();
      this.trackMetric('timeToInteractive', domReadyTime);
    });
  }

  // Observe resource timing
  private observeResourceTiming(): void {
    if (typeof window === 'undefined') return;

    // Monitor API response times
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const startTime = performance.now();
      try {
        const response = await originalFetch(...args);
        const endTime = performance.now();
        const responseTime = endTime - startTime;
        
        this.trackMetric('apiResponseTime', responseTime);
        
        // Track slow API calls
        if (responseTime > 1000) {
          trackEvent('performance', {
            event_category: 'performance',
            metric: 'slow_api_call',
            value: Math.round(responseTime),
            url: typeof args[0] === 'string' ? args[0] : 'unknown',
            user_type: 'guest',
          });
        }
        
        return response;
      } catch (error) {
        const endTime = performance.now();
        const responseTime = endTime - startTime;
        
        this.trackMetric('apiResponseTime', responseTime);
        throw error;
      }
    };

    // Monitor image loading
    const originalImage = window.Image;
    window.Image = (...args: any[]) => {
      const img = new originalImage(...args);
      const startTime = performance.now();
      
      img.addEventListener('load', () => {
        const endTime = performance.now();
        const loadTime = endTime - startTime;
        this.trackMetric('imageLoadTime', loadTime);
      });
      
      return img;
    };
  }

  // Observe memory usage
  private observeMemoryUsage(): void {
    if (typeof window === 'undefined') return;

    // Monitor memory usage if available
    if ('memory' in performance) {
      setInterval(() => {
        const memory = (performance as any).memory;
        if (memory) {
          this.trackMetric('memoryUsage', memory.usedJSHeapSize / 1024 / 1024); // MB
        }
      }, 5000);
    }

    // Monitor DOM node count
    setInterval(() => {
      const nodeCount = document.querySelectorAll('*').length;
      this.trackMetric('domNodes', nodeCount);
    }, 10000);
  }

  // Track a specific metric
  trackMetric(metricName: keyof PerformanceMetrics, value: number): void {
    const currentMetrics = this.metrics.get(window.location.pathname) || this.getDefaultMetrics();
    (currentMetrics as any)[metricName] = value;
    this.metrics.set(window.location.pathname, currentMetrics);
    
    this.notifyObservers(currentMetrics);
    this.checkThresholds(metricName, value);
  }

  // Check if metrics exceed thresholds
  private checkThresholds(metricName: keyof PerformanceMetrics, value: number): void {
    const threshold = (this.thresholds as any)[metricName];
    if (threshold && value > threshold) {
      trackEvent('performance', {
        event_category: 'performance',
        metric: `${metricName}_threshold_exceeded`,
        value: Math.round(value),
        threshold: Math.round(threshold),
        page: window.location.pathname,
        user_type: 'guest',
      });
    }
  }

  // Get default metrics object
  private getDefaultMetrics(): PerformanceMetrics {
    return {
      lcp: 0,
      fid: 0,
      cls: 0,
      ttfb: 0,
      fcp: 0,
      pageLoadTime: 0,
      apiResponseTime: 0,
      imageLoadTime: 0,
      bundleLoadTime: 0,
      memoryUsage: 0,
      networkRequests: 0,
      domNodes: 0,
      timeToInteractive: 0,
      timeToFirstMeaningfulPaint: 0,
    };
  }

  // Get current metrics for a page
  getMetrics(page: string = window.location.pathname): PerformanceMetrics | undefined {
    return this.metrics.get(page);
  }

  // Get all metrics
  getAllMetrics(): Map<string, PerformanceMetrics> {
    return new Map(this.metrics);
  }

  // Subscribe to metrics updates
  subscribe(callback: (metrics: PerformanceMetrics) => void): () => void {
    this.observers.add(callback);
    
    return () => {
      this.observers.delete(callback);
    };
  }

  // Notify observers of metric updates
  private notifyObservers(metrics: PerformanceMetrics): void {
    this.observers.forEach(callback => {
      try {
        callback(metrics);
      } catch (error) {
        console.warn('Error in performance observer:', error);
      }
    });
  }

  // Reset metrics for a page
  resetMetrics(page: string = window.location.pathname): void {
    this.metrics.delete(page);
  }

  // Reset all metrics
  resetAllMetrics(): void {
    this.metrics.clear();
  }

  // Set custom thresholds
  setThresholds(thresholds: Partial<PerformanceThresholds>): void {
    this.thresholds = { ...this.thresholds, ...thresholds };
  }

  // Get current thresholds
  getThresholds(): PerformanceThresholds {
    return { ...this.thresholds };
  }

  // Generate performance report
  generateReport(): {
    summary: {
      totalPages: number;
      averageLCP: number;
      averageFID: number;
      averageCLS: number;
      performanceScore: number;
    };
    details: Array<{
      page: string;
      metrics: PerformanceMetrics;
      score: number;
    }>;
  } {
    const pages = Array.from(this.metrics.entries());
    const totalPages = pages.length;
    
    if (totalPages === 0) {
      return {
        summary: {
          totalPages: 0,
          averageLCP: 0,
          averageFID: 0,
          averageCLS: 0,
          performanceScore: 0,
        },
        details: [],
      };
    }

    const totalLCP = pages.reduce((sum, [_, metrics]) => sum + metrics.lcp, 0);
    const totalFID = pages.reduce((sum, [_, metrics]) => sum + metrics.fid, 0);
    const totalCLS = pages.reduce((sum, [_, metrics]) => sum + metrics.cls, 0);

    const averageLCP = totalLCP / totalPages;
    const averageFID = totalFID / totalPages;
    const averageCLS = totalCLS / totalPages;

    const details = pages.map(([page, metrics]) => ({
      page,
      metrics,
      score: this.calculatePerformanceScore(metrics),
    }));

    const performanceScore = details.reduce((sum, detail) => sum + detail.score, 0) / totalPages;

    return {
      summary: {
        totalPages,
        averageLCP,
        averageFID,
        averageCLS,
        performanceScore,
      },
      details,
    };
  }

  // Calculate performance score (0-100)
  private calculatePerformanceScore(metrics: PerformanceMetrics): number {
    let score = 100;

    // LCP scoring (0-25 points)
    if (metrics.lcp <= 2500) score -= 0;
    else if (metrics.lcp <= 4000) score -= 10;
    else score -= 25;

    // FID scoring (0-25 points)
    if (metrics.fid <= 100) score -= 0;
    else if (metrics.fid <= 300) score -= 10;
    else score -= 25;

    // CLS scoring (0-25 points)
    if (metrics.cls <= 0.1) score -= 0;
    else if (metrics.cls <= 0.25) score -= 10;
    else score -= 25;

    // TTFB scoring (0-25 points)
    if (metrics.ttfb <= 800) score -= 0;
    else if (metrics.ttfb <= 1800) score -= 10;
    else score -= 25;

    return Math.max(0, score);
  }
}

// Export singleton instance
export const performanceMonitor = PerformanceMonitor.getInstance();

// Initialize performance monitoring when module loads
if (typeof window !== 'undefined') {
  // Wait for DOM to be ready before initializing
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      try {
        performanceMonitor.init();
      } catch (error) {
        console.warn('Performance monitoring failed to initialize:', error);
      }
    });
  } else {
    try {
      performanceMonitor.init();
    } catch (error) {
      console.warn('Performance monitoring failed to initialize:', error);
    }
  }
}
